/**
 * Paneler over regattakartene (reprise og live): regattaklokka og løpet som liste.
 * Ligger nede til venstre, løpet over klokka.
 */
import { computeProcedure, type Phase, type RaceRunStatus } from '../lib/startProcedure.ts';
import { FlagGlyph } from './SignalFlags.tsx';

const FASE: Partial<Record<Phase, string>> = {
  warning: 'Varselsignal',
  preparatory: 'Klarsignal',
  one_minute: 'Ett minutt',
  last_10s: 'Ett minutt',
};

/**
 * Regattaklokka: nedtelling med flaggene som er oppe (som på regattasiden), så tid
 * etter start. Står stille på siste båts tid når den er i mål (`iMaal`).
 * `status` er regattaens status på live-kartet (utsatt, avlyst, ferdig).
 */
export function RegattaKlokke({
  msToStart,
  iMaal,
  klasseflagg,
  status = 'scheduled',
}: {
  msToStart: number;
  iMaal: boolean;
  klasseflagg: string[];
  status?: RaceRunStatus;
}) {
  const s = computeProcedure(msToStart, iMaal ? 'started' : status);
  const etter = s.phase === 'started';
  const farge = iMaal
    ? 'border-sky-500/60 text-sky-100'
    : s.isFrozen
    ? 'border-slate-600 text-slate-100'
    : etter
    ? 'border-emerald-500/60 text-emerald-100'
    : s.phase === 'one_minute' || s.phase === 'last_10s'
    ? 'border-amber-500/70 text-amber-100'
    : 'border-yellow-500/60 text-yellow-50';
  const tekst = iMaal
    ? 'Siste båt i mål'
    : s.phase === 'finished'
    ? 'Regattaen er avsluttet'
    : s.phase === 'cancelled'
    ? 'Regattaen er avlyst'
    : s.apFlagUp
    ? 'Utsatt (AP)'
    : etter
    ? -msToStart < 10_000
      ? 'Start!'
      : 'etter start'
    : FASE[s.phase]
    ? `${FASE[s.phase]} · til start`
    : 'til start';
  return (
    <div className={`bg-slate-900/90 border rounded-xl px-3 py-2 shadow-lg shadow-black/40 ${farge}`}>
      <div className="flex items-center gap-3">
        <span className="font-mono font-bold tabular-nums text-3xl leading-none">{s.countdownText}</span>
        {!etter && !s.isFrozen && (
          <span className="flex gap-1">
            {s.classFlagUp && klasseflagg.map((f) => <FlagGlyph key={f} code={f} className="w-8 h-[22px]" />)}
            {s.bluePeterUp && <FlagGlyph code="P" className="w-8 h-[22px]" />}
          </span>
        )}
      </div>
      <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide opacity-80">{tekst}</div>
    </div>
  );
}

/** Løpet i seilingsrekkefølge: «0 - Start», «5B - Fjordbåen», …, «0 - Mål». */
export function LopListe({ rader }: { rader: { kode: string; navn: string }[] }) {
  return (
    <div className="bg-slate-900/90 border border-slate-700 rounded-xl px-3 py-2 shadow-lg shadow-black/40 text-xs text-slate-200">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Løp</div>
      {rader.map((r, i) => (
        <div key={i} className="flex gap-1.5 leading-5 whitespace-nowrap">
          <span className="font-mono font-semibold w-[2.2em]">{r.kode}</span>
          <span>- {r.navn}</span>
        </div>
      ))}
    </div>
  );
}
